export const GOLDEN_INTERACTION_ENTRY_PREFIX = 'rudi:golden-interaction';
const ENTRY_PREFIX = GOLDEN_INTERACTION_ENTRY_PREFIX;

export type GoldenInteractionMilestone =
  'optimistic' | 'durable' | 'acknowledgement' | 'progress' | 'completion';

export interface GoldenInteraction {
  readonly interactionId: string;
  readonly runId: string;
  markRequestStart(): void;
  markResponseReceived(): void;
  markFrontendWorkStart(): void;
  markFrontendWorkComplete(): void;
  markMilestone(milestone: GoldenInteractionMilestone): void;
  complete(usefulResult: GoldenInteractionMilestone): Promise<boolean>;
  cancel(): void;
}

export interface CompletedGoldenInteraction {
  readonly interactionId: string;
  readonly runId: string;
  readonly durationMs: number;
  readonly requestStartOffsetMs?: number;
  readonly responseReceivedOffsetMs?: number;
  readonly frontendWorkMs?: number;
  readonly usefulResultOffsetMs: number;
  readonly presentedFrameOffsetMs: number;
  readonly outcome: 'success';
  readonly cacheStateId?: string;
  readonly dataComplexityBucket?: 'fixed' | 'typical' | 'large' | 'unknown';
  readonly traceCorrelation?: string;
}

export type GoldenInteractionCompletionListener = (interaction: CompletedGoldenInteraction) => void;

const completionListeners = new Set<GoldenInteractionCompletionListener>();

export interface GoldenInteractionLifecycle {
  readonly interactionId: string;
  readonly runId: string;
  readonly phase: 'started' | 'finished';
}

const lifecycleListeners = new Set<(event: GoldenInteractionLifecycle) => void>();

/** Observe input and completion or cancellation, without any screen content. */
export function observeGoldenInteractionLifecycle(
  listener: (event: GoldenInteractionLifecycle) => void
): () => void {
  lifecycleListeners.add(listener);
  return () => lifecycleListeners.delete(listener);
}

function traceCorrelationForWindow(
  performanceApi: Performance,
  startTimeMs: number,
  endTimeMs: number
): string | undefined {
  if (typeof performanceApi.getEntriesByType !== 'function') return undefined;
  const resources = performanceApi.getEntriesByType('resource') as PerformanceResourceTiming[];
  for (let index = resources.length - 1; index >= 0; index -= 1) {
    const resource = resources[index];
    if (resource === undefined) continue;
    if (resource.startTime > endTimeMs || resource.responseEnd < startTimeMs) continue;
    const trace = resource.serverTiming?.find(entry => entry.name === 'traceparent')?.description;
    if (trace !== undefined && trace.length > 0) return trace;
  }
  return undefined;
}

export function observeCompletedGoldenInteractions(
  listener: GoldenInteractionCompletionListener
): () => void {
  completionListeners.add(listener);
  return () => completionListeners.delete(listener);
}

export interface GoldenInteractionOptions {
  readonly actionStartTimeMs?: number;
  readonly requestStartTimeMs?: number;
  readonly cacheStateId?: string;
  readonly dataComplexityBucket?: 'fixed' | 'typical' | 'large' | 'unknown';
  readonly traceCorrelation?: string;
}

const GOLDEN_INTERACTION_OPTION_FIELDS = new Set<keyof GoldenInteractionOptions>([
  'actionStartTimeMs',
  'requestStartTimeMs',
  'cacheStateId',
  'dataComplexityBucket',
  'traceCorrelation',
]);

function assertContentFreeOptions(options: GoldenInteractionOptions): void {
  const unexpectedFields = Object.keys(options).filter(
    field => !GOLDEN_INTERACTION_OPTION_FIELDS.has(field as keyof GoldenInteractionOptions)
  );
  if (unexpectedFields.length > 0) {
    throw new TypeError(
      `Golden Interaction options must stay content-free. Rejected fields: ${unexpectedFields.join(', ')}`
    );
  }
}

let nextRunId = 0;
const trackedInteractions = new Map<string, GoldenInteraction>();
const pendingInteractions = new Map<string, GoldenInteraction>();
const activeInteractions = new Map<string, GoldenInteraction>();

export function hasAnyGoldenInteractionMeasure(): boolean {
  const performanceApi = globalThis.performance;
  if (typeof performanceApi?.getEntriesByType !== 'function') return false;
  return performanceApi
    .getEntriesByType('measure')
    .some(entry => entry.name.startsWith(ENTRY_PREFIX));
}

export function startGoldenInteraction(
  interactionId: string,
  options: GoldenInteractionOptions = {}
): GoldenInteraction {
  assertContentFreeOptions(options);
  nextRunId += 1;
  const runId = String(nextRunId);
  const entryPrefix = `${ENTRY_PREFIX}:${interactionId}:${runId}`;
  const marked = new Set<string>();
  const timestamps = new Map<string, number>();
  let cancelled = false;
  let settled = false;
  let frameId: number | null = null;
  let resolveCompletion: ((presented: boolean) => void) | null = null;
  let completionPromise: Promise<boolean> | null = null;
  let lifecycleFinished = false;
  const finishLifecycle = () => {
    if (lifecycleFinished) return;
    lifecycleFinished = true;
    lifecycleListeners.forEach(listener => listener({ interactionId, runId, phase: 'finished' }));
  };

  const performanceApi = globalThis.performance;
  const canMark = typeof performanceApi?.mark === 'function';
  const canMeasure = typeof performanceApi?.measure === 'function';

  const entryName = (name: string): string => `${entryPrefix}:${name}`;

  const mark = (name: string, startTimeMs?: number): boolean => {
    if (!canMark || marked.has(name)) return false;
    marked.add(name);
    const timestamp = startTimeMs ?? performanceApi?.now?.();
    if (typeof timestamp === 'number') timestamps.set(name, timestamp);
    if (startTimeMs === undefined) {
      performanceApi.mark(entryName(name));
    } else {
      performanceApi.mark(entryName(name), { startTime: startTimeMs });
    }
    if (canMeasure && name !== 'action-start') {
      performanceApi.measure(
        entryName(`action-to-${name}`),
        entryName('action-start'),
        entryName(name)
      );
    }
    return true;
  };

  mark('action-start', options.actionStartTimeMs);
  lifecycleListeners.forEach(listener => listener({ interactionId, runId, phase: 'started' }));

  return {
    interactionId,
    runId,
    markRequestStart: () => mark('request-start', options.requestStartTimeMs),
    markResponseReceived: () => mark('response-received'),
    markFrontendWorkStart: () => mark('frontend-work-start'),
    markFrontendWorkComplete: () => {
      if (!mark('frontend-work-complete')) return;
      if (!canMeasure || !marked.has('frontend-work-start')) return;
      performanceApi.measure(
        entryName('frontend-work'),
        entryName('frontend-work-start'),
        entryName('frontend-work-complete')
      );
    },
    markMilestone: milestone => mark(milestone),
    complete: usefulResult => {
      if (completionPromise !== null) return completionPromise;
      if (cancelled || !canMark || typeof globalThis.requestAnimationFrame !== 'function') {
        completionPromise = Promise.resolve(false);
        finishLifecycle();
        return completionPromise;
      }
      mark(usefulResult);
      mark('useful-result', timestamps.get(usefulResult));

      completionPromise = new Promise(resolve => {
        resolveCompletion = resolve;
        frameId = globalThis.requestAnimationFrame(() => {
          if (cancelled) return;
          frameId = globalThis.requestAnimationFrame(() => {
            if (cancelled) return;
            mark('presented-frame');
            frameId = null;
            resolveCompletion = null;
            settled = true;
            finishLifecycle();
            resolve(true);
            const actionStart = timestamps.get('action-start');
            const presentedFrame = timestamps.get('presented-frame');
            const usefulResultAt = timestamps.get('useful-result');
            if (
              actionStart !== undefined &&
              presentedFrame !== undefined &&
              usefulResultAt !== undefined
            ) {
              const requestStart = timestamps.get('request-start');
              const responseReceived = timestamps.get('response-received');
              const frontendWorkStart = timestamps.get('frontend-work-start');
              const frontendWorkComplete = timestamps.get('frontend-work-complete');
              const completedInteraction: CompletedGoldenInteraction = {
                interactionId,
                runId,
                durationMs: presentedFrame - actionStart,
                usefulResultOffsetMs: usefulResultAt - actionStart,
                presentedFrameOffsetMs: presentedFrame - actionStart,
                outcome: 'success',
              };
              if (options.cacheStateId !== undefined) {
                Object.assign(completedInteraction, { cacheStateId: options.cacheStateId });
              }
              if (options.dataComplexityBucket !== undefined) {
                Object.assign(completedInteraction, {
                  dataComplexityBucket: options.dataComplexityBucket,
                });
              }
              const traceCorrelation =
                options.traceCorrelation ??
                traceCorrelationForWindow(performanceApi, actionStart, presentedFrame);
              if (traceCorrelation !== undefined) {
                Object.assign(completedInteraction, { traceCorrelation });
              }
              if (requestStart !== undefined) {
                Object.assign(completedInteraction, {
                  requestStartOffsetMs: requestStart - actionStart,
                });
              }
              if (responseReceived !== undefined) {
                Object.assign(completedInteraction, {
                  responseReceivedOffsetMs: responseReceived - actionStart,
                });
              }
              if (frontendWorkStart !== undefined && frontendWorkComplete !== undefined) {
                Object.assign(completedInteraction, {
                  frontendWorkMs: frontendWorkComplete - frontendWorkStart,
                });
              }
              completionListeners.forEach(listener => listener(completedInteraction));
            }
          });
        });
      });
      return completionPromise;
    },
    cancel: () => {
      if (cancelled || settled) return;
      cancelled = true;
      settled = true;
      mark('cancelled');
      if (frameId !== null && typeof globalThis.cancelAnimationFrame === 'function') {
        globalThis.cancelAnimationFrame(frameId);
      }
      frameId = null;
      resolveCompletion?.(false);
      resolveCompletion = null;
      finishLifecycle();
    },
  };
}

export function beginGoldenInteraction(interactionId: string): void {
  activeInteractions.get(interactionId)?.cancel();
  const interaction = startGoldenInteraction(interactionId);
  interaction.markFrontendWorkStart();
  activeInteractions.set(interactionId, interaction);
}

export function completeGoldenInteraction(interactionId: string): Promise<boolean> {
  const interaction = activeInteractions.get(interactionId);
  if (!interaction) return Promise.resolve(false);
  activeInteractions.delete(interactionId);
  interaction.markFrontendWorkComplete();
  return interaction.complete('completion');
}

export function cancelGoldenInteraction(interactionId: string): void {
  const interaction = activeInteractions.get(interactionId);
  if (!interaction) return;
  activeInteractions.delete(interactionId);
  interaction.cancel();
}

export function restartGoldenInteraction(interactionId: string): GoldenInteraction {
  activeInteractions.get(interactionId)?.cancel();
  const interaction = startGoldenInteraction(interactionId);
  activeInteractions.set(interactionId, interaction);
  return interaction;
}

export function completeActiveGoldenInteraction(
  interactionId: string,
  usefulResult: GoldenInteractionMilestone
): Promise<boolean> {
  const interaction = activeInteractions.get(interactionId);
  if (!interaction) return Promise.resolve(false);
  activeInteractions.delete(interactionId);
  return interaction.complete(usefulResult);
}

export function startTrackedGoldenInteraction(
  interactionId: string,
  options: GoldenInteractionOptions = {}
): GoldenInteraction {
  trackedInteractions.get(interactionId)?.cancel();
  const interaction = startGoldenInteraction(interactionId, options);
  trackedInteractions.set(interactionId, interaction);
  return interaction;
}

export function getTrackedGoldenInteraction(interactionId: string): GoldenInteraction | undefined {
  return trackedInteractions.get(interactionId);
}

export async function completeTrackedGoldenInteraction(
  interactionId: string,
  usefulResult: GoldenInteractionMilestone
): Promise<boolean> {
  const interaction = trackedInteractions.get(interactionId);
  if (interaction === undefined) return false;
  const presented = await interaction.complete(usefulResult);
  if (trackedInteractions.get(interactionId) === interaction) {
    trackedInteractions.delete(interactionId);
  }
  return presented;
}

export function startPendingGoldenInteraction(interactionId: string): GoldenInteraction {
  pendingInteractions.get(interactionId)?.cancel();
  const interaction = startGoldenInteraction(interactionId);
  pendingInteractions.set(interactionId, interaction);
  return interaction;
}

export function takePendingGoldenInteraction(interactionId: string): GoldenInteraction | null {
  const interaction = pendingInteractions.get(interactionId) ?? null;
  pendingInteractions.delete(interactionId);
  return interaction;
}
